import React from "react";

import { motion, useReducedMotion } from "framer-motion";

import { ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";

import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

import LandingMenu from "../../../../components/layout/LandingMenu";
import LoginButton from "../../../../components/layout/LoginButton";
import LanguageSwitcher from "../../../../components/LanguageSwitcher";

import heroImage from "../../../../assets/home/hero-image.jpg";
import legupLogo from "../../../../assets/legup-logo.svg";

import renovationImage from "../../../../assets/about/about-renovation.webp";
import consultationImage from "../../../../assets/about/about-consultation.webp";
import planningImage from "../../../../assets/about/about-planning.webp";
import homeownersImage from "../../../../assets/about/about-homeowners.webp";
import energyImage from "../../../../assets/about/about-energy.webp";

import "./HomeHeroSection.css";
import "./HomeHeroAboutSection.css";

gsap.registerPlugin(ScrollTrigger);

const DEBUG_SCROLL = false;

function AboutTitle({ t }) {
  const line1 = [
    {
      text: t("about.title.prefix", "We bridge the gap between"),
      accent: false,
    },
    {
      text: t("about.title.affordable", "affordable"),
      accent: true,
    },
  ];

  const line2 = [
    {
      text: t("about.title.finance", "finance"),
      accent: true,
    },
    {
      text: t("about.title.and", "and"),
      accent: false,
    },
    {
      text: t("about.title.renovation", "deep renovation."),
      accent: true,
    },
  ];

  const fullText = [...line1, ...line2].map((chunk) => chunk.text).join(" ");

  const renderLine = (chunks, lineKey) => (
    <span className="home-about-gsap__title-row" key={lineKey}>
      {chunks.map((chunk, index) => (
        <span
          key={`${lineKey}-${chunk.text}-${index}`}
          className="home-about-gsap__title-chunk-mask"
          aria-hidden="true"
        >
          <span
            className={`home-about-gsap__title-chunk ${
              chunk.accent ? "is-accent" : ""
            }`}
          >
            {chunk.text}
          </span>
        </span>
      ))}
    </span>
  );

  return (
    <span className="home-about-gsap__title-line" aria-label={fullText}>
      <span className="sr-only">{fullText}</span>

      {renderLine(line1, "line-1")}

      {renderLine(line2, "line-2")}
    </span>
  );
}

function BodyBlock({ text, className, highlights = [] }) {
  if (!highlights.length) {
    return (
      <span className={`home-about-gsap__body-block ${className}`}>{text}</span>
    );
  }

  const escapedHighlights = highlights
    .filter(Boolean)
    .map((value) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));

  const pattern = escapedHighlights.length
    ? new RegExp(`(${escapedHighlights.join("|")})`, "gi")
    : null;

  const pieces = pattern ? text.split(pattern) : [text];

  return (
    <span className={`home-about-gsap__body-block ${className}`}>
      {pieces.map((piece, index) => {
        const isHighlight = highlights.some(
          (highlight) =>
            piece.toLocaleLowerCase() === highlight.toLocaleLowerCase(),
        );

        return isHighlight ? (
          <strong key={`${piece}-${index}`}>{piece}</strong>
        ) : (
          <React.Fragment key={`${piece}-${index}`}>{piece}</React.Fragment>
        );
      })}
    </span>
  );
}

export default function HomeHeroAboutSection({ active = true }) {
  const { t, i18n } = useTranslation("home");
  const prefersReducedMotion = useReducedMotion();

  const sceneRef = React.useRef(null);
  const stageRef = React.useRef(null);
  const imageShellRef = React.useRef(null);
  const imageTargetRef = React.useRef(null);
  const aboutRootRef = React.useRef(null);

  const heroHeaderRef = React.useRef(null);
  const heroStatementRef = React.useRef(null);
  const heroCardRef = React.useRef(null);

  const pointerFrameRef = React.useRef(null);
  const cursorInfluenceRef = React.useRef(0);
  const cursorWasActiveRef = React.useRef(false);
  const scrollVisualModeRef = React.useRef(false);
  const aboutRevealPlayedRef = React.useRef(false);

  const [imageLoaded, setImageLoaded] = React.useState(false);
  const [heroForegroundReady, setHeroForegroundReady] = React.useState(false);
  const [heroEntranceDone, setHeroEntranceDone] = React.useState(false);

  const shouldExpand = active && imageLoaded;

  React.useEffect(() => {
    if (!stageRef.current) {
      return undefined;
    }

    const decodeImages = () => {
      const images =
        stageRef.current?.querySelectorAll(
          ".home-about-gsap__image-card img",
        ) || [];

      images.forEach((img) => {
        if (typeof img.decode === "function") {
          img.decode().catch(() => {});
        }
      });
    };

    let idleId = null;
    let timeoutId = null;

    if (typeof window.requestIdleCallback === "function") {
      idleId = window.requestIdleCallback(decodeImages, {
        timeout: 1200,
      });
    } else {
      timeoutId = window.setTimeout(decodeImages, 180);
    }

    return () => {
      if (idleId !== null && typeof window.cancelIdleCallback === "function") {
        window.cancelIdleCallback(idleId);
      }

      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }
    };
  }, []);

  /* =======================================================
     ORIGINAL HERO ENTRANCE
     ======================================================= */

  React.useEffect(() => {
    if (!shouldExpand) {
      setHeroForegroundReady(false);
      setHeroEntranceDone(false);
      return undefined;
    }

    if (prefersReducedMotion) {
      setHeroForegroundReady(true);
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setHeroForegroundReady(true);
    }, 760);

    return () => window.clearTimeout(timer);
  }, [shouldExpand, prefersReducedMotion]);

  const statementText = t(
    "hero.statement",
    "LINKING RENOVATION, FINANCING AND IMPLEMENTATION",
  );

  const statementWords = statementText.split(" ");

  const headerVariants = {
    hidden: {
      opacity: 0,
      y: prefersReducedMotion ? 0 : -16,
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: prefersReducedMotion ? 0.01 : 0.58,
        ease: [0.22, 1, 0.36, 1],
        staggerChildren: prefersReducedMotion ? 0 : 0.07,
      },
    },
  };

  const headerItemVariants = {
    hidden: {
      opacity: 0,
      y: prefersReducedMotion ? 0 : -10,
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: prefersReducedMotion ? 0.01 : 0.48,
        ease: [0.22, 1, 0.36, 1],
      },
    },
  };

  const statementVariants = {
    hidden: {},
    visible: {
      transition: {
        delayChildren: prefersReducedMotion ? 0 : 0.1,
        staggerChildren: prefersReducedMotion ? 0 : 0.055,
      },
    },
  };

  const statementWordVariants = {
    hidden: {
      opacity: 0,
      y: prefersReducedMotion ? "0%" : "115%",
    },
    visible: {
      opacity: 1,
      y: "0%",
      transition: {
        duration: prefersReducedMotion ? 0.01 : 0.56,
        ease: [0.22, 1, 0.36, 1],
      },
    },
  };

  const cardVariants = {
    hidden: {
      opacity: 0,
      y: prefersReducedMotion ? 0 : 34,
      scale: prefersReducedMotion ? 1 : 0.975,
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        delay: prefersReducedMotion ? 0 : 0.3,
        duration: prefersReducedMotion ? 0.01 : 0.78,
        ease: [0.22, 1, 0.36, 1],
      },
    },
  };

  /* =======================================================
     CURSOR FOLLOW
     ======================================================= */

  const resetPointer = React.useCallback(() => {
    if (!stageRef.current) return;

    const targets = [
      imageShellRef.current,
      ...stageRef.current.querySelectorAll(".home-about-gsap__image-card"),
    ].filter(Boolean);

    gsap.to(targets, {
      rotationX: 0,
      rotationY: 0,
      duration: 0.45,
      ease: "power3.out",
      overwrite: "auto",
    });
  }, []);

  const handlePointerMove = React.useCallback(
    (event) => {
      if (
        prefersReducedMotion ||
        !stageRef.current ||
        cursorInfluenceRef.current <= 0.001
      ) {
        return;
      }

      if (pointerFrameRef.current) {
        cancelAnimationFrame(pointerFrameRef.current);
      }

      pointerFrameRef.current = requestAnimationFrame(() => {
        const rect = stageRef.current.getBoundingClientRect();

        const nx = Math.max(
          -1,
          Math.min(1, ((event.clientX - rect.left) / rect.width) * 2 - 1),
        );

        const ny = Math.max(
          -1,
          Math.min(1, ((event.clientY - rect.top) / rect.height) * 2 - 1),
        );

        const influence = cursorInfluenceRef.current;

        if (imageShellRef.current) {
          gsap.to(imageShellRef.current, {
            rotationY: nx * 3.6 * influence,
            rotationX: -ny * 3.6 * influence,
            transformPerspective: 1400,
            duration: 0.42,
            ease: "power3.out",
            overwrite: "auto",
          });
        }

        stageRef.current
          .querySelectorAll(".home-about-gsap__image-card")
          .forEach((card) => {
            const depth = Number(card.dataset.depth || 1);

            gsap.to(card, {
              rotationY: nx * 4.5 * depth * influence,
              rotationX: -ny * 4.5 * depth * influence,
              transformPerspective: 1400,
              duration: 0.42,
              ease: "power3.out",
              overwrite: "auto",
            });
          });

        pointerFrameRef.current = null;
      });
    },
    [prefersReducedMotion],
  );

  /* =======================================================
     SCROLL TIMELINE
     ======================================================= */

  React.useLayoutEffect(() => {
    if (
      !active ||
      !heroEntranceDone ||
      !sceneRef.current ||
      !stageRef.current ||
      !imageShellRef.current ||
      !imageTargetRef.current ||
      !aboutRootRef.current
    ) {
      return undefined;
    }

    const ctx = gsap.context(() => {
      const scene = sceneRef.current;
      const stage = stageRef.current;
      const image = imageShellRef.current;
      const target = imageTargetRef.current;
      const aboutRoot = aboutRootRef.current;

      const aboutCards = gsap.utils.toArray(
        ".home-about-gsap__image-card",
        stage,
      );

      const eyebrowText = stage.querySelector(".home-about-gsap__eyebrow-text");

      const titleChunks = gsap.utils.toArray(
        ".home-about-gsap__title-chunk",
        stage,
      );

      const body1 = stage.querySelector(".home-about-gsap__body-1");

      const body2 = stage.querySelector(".home-about-gsap__body-2");

      const body3 = stage.querySelector(".home-about-gsap__body-3");

      gsap.set(aboutRoot, {
        visibility: "visible",
      });

      aboutCards.forEach((card) => {
        const reveal = card.dataset.reveal;

        const offset = {
          x: 0,
          y: 0,
        };

        if (reveal === "left") {
          offset.x = -42;
        } else if (reveal === "right") {
          offset.x = 42;
        } else if (reveal === "top") {
          offset.y = -36;
        } else if (reveal === "bottom") {
          offset.y = 36;
        }

        gsap.set(card, {
          autoAlpha: 0,
          x: offset.x,
          y: offset.y,
          scale: 0.985,
          rotationX: 0,
          rotationY: 0,
          force3D: true,
        });
      });

      gsap.set(eyebrowText, {
        autoAlpha: 0,
        y: 16,
      });

      gsap.set(titleChunks, {
        autoAlpha: 0,
        y: 28,
      });

      gsap.set([body1, body2, body3], {
        autoAlpha: 0,
        y: 14,
      });

      const targetGeometry = () => {
        const stageRect = stage.getBoundingClientRect();
        const targetRect = target.getBoundingClientRect();

        const baseWidth = stageRect.width;
        const baseHeight = stageRect.height;

        const targetWidth = targetRect.width;
        const targetHeight = targetRect.height;

        const scale = Math.max(
          targetWidth / baseWidth,
          targetHeight / baseHeight,
        );

        const scaledWidth = baseWidth * scale;
        const scaledHeight = baseHeight * scale;

        const cropScaledX = Math.max(0, (scaledWidth - targetWidth) / 2);

        const cropScaledY = Math.max(0, (scaledHeight - targetHeight) / 2);

        const cropSourceX = scale > 0 ? cropScaledX / scale : 0;

        const cropSourceY = scale > 0 ? cropScaledY / scale : 0;

        const targetRadius = 18;

        const sourceRadius = scale > 0 ? targetRadius / scale : targetRadius;

        return {
          x: targetRect.left - stageRect.left - cropScaledX,

          y: targetRect.top - stageRect.top - cropScaledY,

          scale,

          clipPath:
            `inset(` +
            `${cropSourceY}px ` +
            `${cropSourceX}px ` +
            `${cropSourceY}px ` +
            `${cropSourceX}px ` +
            `round ${sourceRadius}px` +
            `)`,

          borderRadius: sourceRadius,
        };
      };

      const scrollDistance = () => {
        const vh = window.innerHeight;

        if (window.innerWidth <= 760) {
          return Math.round(vh * 3.0);
        }

        if (window.innerWidth <= 1150) {
          return Math.round(vh * 2.65);
        }

        return Math.round(vh * 2.35);
      };

      /*
       * Extra pinned scroll AFTER the Hero → About animation
       * has completely finished.
       *
       * This is real breathing room: the completed About state
       * remains fixed while the user keeps scrolling, and only
       * after this distance does the next page section appear.
       */
      const endHoldDistance = () => {
        const vh = window.innerHeight;

        if (window.innerWidth <= 760) {
          return Math.round(vh * 0.42);
        }

        if (window.innerWidth <= 1150) {
          return Math.round(vh * 0.52);
        }

        return Math.round(vh * 0.62);
      };

      const totalScrollDistance = () => scrollDistance() + endHoldDistance();

      const animationScrollRatio = () =>
        scrollDistance() / totalScrollDistance();

      gsap.set(image, {
        x: 0,
        y: 0,
        scale: 1,

        clipPath: "inset(0px 0px 0px 0px round 0px)",

        borderRadius: 0,

        transformOrigin: "0 0",

        force3D: true,
      });

      /*
       * =====================================================
       * PHASE 1 — SCROLL-SCRUBBED HERO TRAVEL ONLY
       * =====================================================
       */
      const timeline = gsap.timeline({
        paused: true,

        defaults: {
          ease: "none",
        },
      });

      timeline.to(
        image,

        {
          x: () => targetGeometry().x,

          y: () => targetGeometry().y,

          scale: () => targetGeometry().scale,

          clipPath: () => targetGeometry().clipPath,

          borderRadius: () => targetGeometry().borderRadius,

          force3D: true,

          duration: 0.58,

          ease: "none",
        },

        0,
      );

      timeline.to(
        heroStatementRef.current,
        {
          autoAlpha: 0,
          y: -22,

          duration: 0.24,

          ease: "power1.out",
        },
        0.14,
      );

      timeline.to(
        heroCardRef.current,
        {
          autoAlpha: 0,
          y: -30,
          duration: 0.27,
          ease: "power1.out",
        },
        0.18,
      );

      timeline.to(
        heroHeaderRef.current,
        {
          autoAlpha: 0,
          y: -14,

          duration: 0.24,

          ease: "power1.out",
        },
        0.22,
      );

      timeline.to(
        {},
        {
          duration: 0.42,
        },
        0.58,
      );

      /*
       * =====================================================
       * PHASE 2 — NORMAL ABOUT REVEAL
       * =====================================================
       */
      const aboutReveal = gsap.timeline({
        paused: true,

        defaults: {
          overwrite: "auto",
        },

        onComplete: () => {
          scene.classList.add("is-about-settled");
        },

        onReverseComplete: () => {
          scene.classList.remove("is-about-settled");
        },
      });

      aboutCards.forEach((card, index) => {
        aboutReveal.to(
          card,
          {
            autoAlpha: 1,
            x: 0,
            y: 0,
            scale: 1,

            duration: 0.42,

            ease: "power3.out",

            force3D: true,
          },
          index * 0.085,
        );
      });

      aboutReveal.to(
        eyebrowText,
        {
          autoAlpha: 1,
          y: 0,

          duration: 0.34,

          ease: "power3.out",
        },
        0.44,
      );

      aboutReveal.to(
        titleChunks,
        {
          autoAlpha: 1,
          y: 0,

          duration: 0.46,

          stagger: 0.065,

          ease: "power3.out",
        },
        0.5,
      );

      aboutReveal.to(
        body1,
        {
          autoAlpha: 1,
          y: 0,

          duration: 0.38,

          ease: "power3.out",
        },
        0.76,
      );

      aboutReveal.to(
        body2,
        {
          autoAlpha: 1,
          y: 0,

          duration: 0.38,

          ease: "power3.out",
        },
        0.86,
      );

      aboutReveal.to(
        body3,
        {
          autoAlpha: 1,
          y: 0,

          duration: 0.38,

          ease: "power3.out",
        },
        0.96,
      );

      const ABOUT_TRIGGER_PROGRESS = 0.65;

      timeline.progress(0);

      const trigger = ScrollTrigger.create({
        trigger: scene,

        start: "top top",

        /*
         * Animation distance + genuine end breathing room.
         */
        end: () => `+=${totalScrollDistance()}`,

        pin: true,

        pinSpacing: true,

        anticipatePin: 1,

        invalidateOnRefresh: true,

        markers: DEBUG_SCROLL,

        onUpdate: (self) => {
          /*
           * Only the first portion of the pinned scroll controls
           * the Hero → About travel. During the remaining portion,
           * activeProgress stays at 1 and the finished About state
           * simply remains on screen.
           */
          const activeProgress = gsap.utils.clamp(
            0,
            1,
            self.progress / animationScrollRatio(),
          );

          timeline.progress(activeProgress);

          const scrollVisualMode = activeProgress > 0.004;

          if (scrollVisualModeRef.current !== scrollVisualMode) {
            scrollVisualModeRef.current = scrollVisualMode;

            scene.classList.toggle("is-scroll-active", scrollVisualMode);
          }

          if (
            activeProgress >= ABOUT_TRIGGER_PROGRESS &&
            !aboutRevealPlayedRef.current
          ) {
            aboutRevealPlayedRef.current = true;

            aboutReveal.timeScale(1).play();
          } else if (
            activeProgress < ABOUT_TRIGGER_PROGRESS &&
            aboutRevealPlayedRef.current
          ) {
            aboutRevealPlayedRef.current = false;

            aboutReveal.timeScale(2.6).reverse();
          }

          const influence = gsap.utils.clamp(
            0,
            1,
            (activeProgress - 0.9) / 0.08,
          );

          cursorInfluenceRef.current = influence;

          const isActive = influence > 0.001;

          if (cursorWasActiveRef.current && !isActive) {
            resetPointer();
          }

          cursorWasActiveRef.current = isActive;
        },
      });

      return () => {
        trigger.kill();

        timeline.kill();

        aboutReveal.kill();
      };
    }, sceneRef);

    const refresh = () => ScrollTrigger.refresh();

    const frame = requestAnimationFrame(() => {
      requestAnimationFrame(refresh);
    });

    window.addEventListener("resize", refresh);

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("resize", refresh);

      if (pointerFrameRef.current) {
        cancelAnimationFrame(pointerFrameRef.current);
      }

      cursorInfluenceRef.current = 0;

      cursorWasActiveRef.current = false;

      scrollVisualModeRef.current = false;

      aboutRevealPlayedRef.current = false;

      sceneRef.current?.classList.remove("is-scroll-active");

      sceneRef.current?.classList.remove("is-about-settled");

      ctx.revert();
    };
  }, [active, heroEntranceDone, i18n.resolvedLanguage, resetPointer]);

  const eyebrow = t("about.eyebrow", "WHY LEG-UP EXISTS");

  const body1Text = t(
    "about.body.p1",
    "LEG-UP turns a complex renovation journey into one connected experience, bringing property, financial and supporting information together in a single digital dossier.",
  );

  const body2Text = t(
    "about.body.p2",
    "Homebuyers can move seamlessly between renovation planning and financing, connecting with One-Stop-Shops and lenders while documents, assessments and decisions remain part of the same coordinated process.",
  );

  const body3Text = t(
    "about.body.p3",
    "From the first renovation steps to funding, invoices and project follow-up, LEG-UP keeps every actor aligned and every stage visible—making renovation easier to understand, manage and move forward.",
  );

  return (
    <section
      ref={sceneRef}
      className={`home-hero-about-gsap${
        heroEntranceDone ? " is-hero-ready" : ""
      }`}
    >
      <div
        ref={stageRef}
        className="home-hero-about-gsap__stage"
        onPointerMove={handlePointerMove}
        onPointerLeave={resetPointer}
      >
        <section
          className="home-hero home-hero-about-gsap__hero"
          aria-label={t("hero.sectionLabel", "LEG-UP platform introduction")}
        >
          <div className="home-hero__canvas">
            <div
              ref={imageShellRef}
              className="home-hero-about-gsap__image-shell"
            >
              <motion.div
                className="home-hero-about-gsap__image-frame"
                initial={{
                  height: "var(--home-hero-collapsed-height)",
                  opacity: 0,
                }}
                animate={
                  shouldExpand
                    ? {
                        height: "var(--home-hero-expanded-height)",
                        opacity: 1,
                      }
                    : {
                        height: "var(--home-hero-collapsed-height)",
                        opacity: active && imageLoaded ? 1 : 0,
                      }
                }
                transition={{
                  height: {
                    duration: prefersReducedMotion ? 0.01 : 2.15,
                    delay: prefersReducedMotion ? 0 : 0.14,
                    ease: [0.22, 1, 0.36, 1],
                  },
                  opacity: {
                    duration: prefersReducedMotion ? 0.01 : 0.34,
                  },
                }}
                onAnimationComplete={() => {
                  if (shouldExpand) {
                    setHeroEntranceDone(true);
                  }
                }}
              >
                <motion.img
                  className="home-hero__image"
                  src={heroImage}
                  alt={t(
                    "hero.imageAlt",
                    "Trees growing from stacks of coins, representing investment in energy-efficient renovation",
                  )}
                  onLoad={() => setImageLoaded(true)}
                  initial={{ scale: 1.06 }}
                  animate={{
                    scale: shouldExpand ? 1 : 1.06,
                  }}
                  transition={{
                    duration: prefersReducedMotion ? 0.01 : 2.35,
                    delay: prefersReducedMotion ? 0 : 0.08,
                    ease: [0.22, 1, 0.36, 1],
                  }}
                />

                <div className="home-hero__image-overlay" aria-hidden="true" />
              </motion.div>
            </div>

            {heroForegroundReady && (
              <div className="home-hero__overlay">
                <motion.header
                  ref={heroHeaderRef}
                  className="home-hero__header"
                  initial="hidden"
                  animate="visible"
                  variants={headerVariants}
                >
                  <motion.div
                    className="home-hero__header-left"
                    variants={headerItemVariants}
                  >
                    <LandingMenu />
                  </motion.div>

                  <motion.a
                    className="home-hero__brand"
                    href="/"
                    aria-label="LEG-UP"
                    variants={headerItemVariants}
                  >
                    <img src={legupLogo} alt="LEG-UP" />
                  </motion.a>

                  <motion.div
                    className="home-hero__header-right"
                    variants={headerItemVariants}
                  >
                    <LanguageSwitcher />
                    <LoginButton />
                  </motion.div>
                </motion.header>

                <div className="home-hero__content">
                  <motion.div
                    ref={heroStatementRef}
                    className="home-hero__statement"
                    initial="hidden"
                    animate="visible"
                    variants={statementVariants}
                  >
                    <span
                      className="home-hero__statement-line"
                      aria-hidden="true"
                    >
                      {statementWords.map((word, index) => (
                        <span
                          key={`${word}-${index}`}
                          className="home-hero__statement-word-mask"
                        >
                          <motion.span
                            className="home-hero__statement-word"
                            variants={statementWordVariants}
                          >
                            {word}
                          </motion.span>
                        </span>
                      ))}
                    </span>

                    <span className="sr-only">{statementText}</span>
                  </motion.div>

                  <motion.article
                    ref={heroCardRef}
                    className="home-hero__card"
                    initial="hidden"
                    animate="visible"
                    variants={cardVariants}
                  >
                    <div className="home-hero__card-glow" aria-hidden="true" />

                    <div className="home-hero__card-header">
                      <span className="home-hero__card-marker" />

                      <span className="home-hero__card-kicker">
                        {t("hero.card.kicker", "FOR HOMEBUYERS")}
                      </span>
                    </div>

                    <h1 className="home-hero__title">
                      {t(
                        "hero.card.title",
                        "Your renovation journey, organised in one place.",
                      )}
                    </h1>

                    <p className="home-hero__description">
                      {t(
                        "hero.card.description",
                        "Create and manage your homebuyer dossier, provide your financial and property information, upload the required documents and connect with the organisations supporting your renovation. Through LEG-UP, you can follow the renovation planning process, receive and review financing options, manage the funding linked to your project and keep track of invoices and renovation progress from one digital workspace.",
                      )}
                    </p>

                    <div className="home-hero__card-actions">
                      <motion.a
                        className="home-hero__primary-cta"
                        href="/register/homebuyer"
                        whileHover={
                          prefersReducedMotion ? undefined : { y: -2 }
                        }
                        whileTap={
                          prefersReducedMotion ? undefined : { scale: 0.985 }
                        }
                      >
                        <span>
                          {t(
                            "hero.card.primaryCta",
                            "Create Homebuyer account",
                          )}
                        </span>

                        <span className="home-hero__primary-arrow">
                          <ArrowRight size={17} />
                        </span>
                      </motion.a>
                    </div>
                  </motion.article>
                </div>
              </div>
            )}
          </div>
        </section>

        <div ref={aboutRootRef} className="home-about-gsap">
          <div className="home-about-gsap__collage">
            <div
              className="home-about-gsap__image-card home-about-gsap__image--renovation"
              data-reveal="bottom"
              data-depth="0.85"
            >
              <img src={renovationImage} alt="" />
            </div>

            <div
              className="home-about-gsap__image-card home-about-gsap__image--consultation"
              data-reveal="left"
              data-depth="1.05"
            >
              <img src={consultationImage} alt="" />
            </div>

            <div
              className="home-about-gsap__image-card home-about-gsap__image--planning"
              data-reveal="right"
              data-depth="0.95"
            >
              <img src={planningImage} alt="" />
            </div>

            <div
              className="home-about-gsap__image-card home-about-gsap__image--homeowners"
              data-reveal="top"
              data-depth="0.9"
            >
              <img src={homeownersImage} alt="" />
            </div>

            <div
              className="home-about-gsap__image-card home-about-gsap__image--energy"
              data-reveal="left"
              data-depth="1"
            >
              <img src={energyImage} alt="" />
            </div>

            <div
              ref={imageTargetRef}
              className="home-about-gsap__image-target"
              aria-hidden="true"
            />

            <div className="home-about-gsap__halo" aria-hidden="true" />
          </div>

          <div className="home-about-gsap__copy">
            <div className="home-about-gsap__eyebrow">
              <span className="home-about-gsap__eyebrow-mask">
                <span className="home-about-gsap__eyebrow-text">{eyebrow}</span>
              </span>
            </div>

            <h2 className="home-about-gsap__title">
              <AboutTitle t={t} />
            </h2>

            <div className="home-about-gsap__body">
              <p>
                <BodyBlock
                  className="home-about-gsap__body-1"
                  text={body1Text}
                  highlights={[
                    "one connected experience",
                    "single digital dossier",
                  ]}
                />
              </p>

              <p>
                <BodyBlock
                  className="home-about-gsap__body-2"
                  text={body2Text}
                  highlights={[
                    "renovation planning and financing",
                    "One-Stop-Shops and lenders",
                  ]}
                />
              </p>

              <p>
                <BodyBlock
                  className="home-about-gsap__body-3"
                  text={body3Text}
                  highlights={[
                    "funding, invoices and project follow-up",
                    "every actor aligned",
                    "every stage visible",
                  ]}
                />
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
